import { create } from 'zustand'

import { chatClient } from '@/api/chat/chat-client'
import { chatsApi } from '@/api/chat/chats-api'
import type {
  ChatClientEvent,
  ChatResultFrame,
  ChatSummaryDto,
  ChatTurnDto,
} from '@/api/chat/chat-types'
import type { ChatMessage, ChatProgress } from '@/features/chat/model/chat.types'

const ACTIVE_CHAT_KEY = 'weather-orchestra.active-chat-id'
const MAX_MESSAGE_LENGTH = 4000

let messageSeq = 0
let operationGeneration = 0
function nextMessageId(prefix = 'msg'): string {
  messageSeq += 1
  return `${prefix}-${messageSeq}`
}

function titleFromPrompt(prompt: string): string {
  const trimmed = prompt.trim().replace(/\s+/g, ' ')
  return trimmed.length > 40 ? `${trimmed.slice(0, 40)}…` : trimmed
}

interface ChatStore {
  title: string | null
  chatId: string | null
  chats: ChatSummaryDto[]
  chatTotal: number
  messages: ChatMessage[]
  result: ChatResultFrame | null
  resultCompletedAt: string | null
  previewMessageId: string | null
  isSending: boolean
  isWriter: boolean
  awaitingAnswer: boolean
  isDemoted: boolean
  isReadOnly: boolean
  isLoadingChats: boolean
  initialized: boolean
  inFlightRequestId: string | null
  progress: ChatProgress[]
  initialize: (viewerId: string) => Promise<void>
  refreshChats: () => Promise<void>
  loadMoreChats: () => Promise<void>
  sendPrompt: (prompt: string, attachmentIds?: string[]) => Promise<void>
  openChat: (chatId: string, viewerId: string) => Promise<void>
  deleteChat: (chatId: string) => Promise<void>
  takeOver: () => Promise<void>
  selectPreview: (messageId: string) => void
  loadTrace: (messageId: string) => Promise<void>
  reset: () => void
  clear: () => void
}

const EMPTY_CHAT_STATE = {
  title: null,
  chatId: null,
  messages: [] as ChatMessage[],
  result: null,
  resultCompletedAt: null,
  previewMessageId: null,
  isSending: false,
  isWriter: false,
  awaitingAnswer: false,
  isDemoted: false,
  isReadOnly: false,
  inFlightRequestId: null,
  progress: [] as ChatProgress[],
}

export const useChatStore = create<ChatStore>((set, get) => ({
  ...EMPTY_CHAT_STATE,
  chats: [],
  chatTotal: 0,
  isLoadingChats: false,
  initialized: false,

  initialize: async (viewerId) => {
    if (get().initialized) return
    set({ initialized: true })
    const savedChatId = localStorage.getItem(ACTIVE_CHAT_KEY)
    const results = await Promise.allSettled([
      get().refreshChats(),
      ...(savedChatId ? [get().openChat(savedChatId, viewerId)] : []),
    ])
    if (savedChatId && results[1]?.status === 'rejected') {
      localStorage.removeItem(ACTIVE_CHAT_KEY)
    }
  },

  refreshChats: async () => {
    set({ isLoadingChats: true })
    try {
      const response = await chatsApi.list()
      set({ chats: response.items, chatTotal: response.total })
    } finally {
      set({ isLoadingChats: false })
    }
  },

  loadMoreChats: async () => {
    const state = get()
    if (state.isLoadingChats || state.chats.length >= state.chatTotal) return
    set({ isLoadingChats: true })
    try {
      const response = await chatsApi.list(20, state.chats.length)
      set((current) => ({
        chats: [
          ...current.chats,
          ...response.items.filter(
            (item) => !current.chats.some((chat) => chat.chat_id === item.chat_id),
          ),
        ],
        chatTotal: response.total,
      }))
    } finally {
      set({ isLoadingChats: false })
    }
  },

  sendPrompt: async (prompt, attachmentIds = []) => {
    const text = prompt.trim()
    const state = get()
    if (
      !text ||
      text.length > MAX_MESSAGE_LENGTH ||
      state.isSending ||
      state.isDemoted ||
      state.isReadOnly
    ) {
      return
    }

    set((current) => ({
      title: current.title ?? titleFromPrompt(text),
      messages: [
        ...current.messages,
        {
          id: nextMessageId('optimistic'),
          role: 'user',
          content: text,
          optimistic: true,
        },
      ],
      result: null,
      resultCompletedAt: null,
      previewMessageId: null,
      isSending: true,
      awaitingAnswer: false,
      progress: [],
    }))

    const operation = ++operationGeneration
    try {
      let chatId = get().chatId
      if (!chatId) {
        const [chat] = await Promise.all([chatsApi.create(), chatClient.connect()])
        if (operation !== operationGeneration) return
        chatId = chat.chat_id
        localStorage.setItem(ACTIVE_CHAT_KEY, chatId)
        set({ chatId })
      }
      await chatClient.attach(chatId)
      if (operation !== operationGeneration) return
      const requestId = chatClient.sendMessage(text, undefined, attachmentIds)
      set({ inFlightRequestId: requestId })
    } catch (error) {
      if (operation !== operationGeneration) return
      appendAssistant(errorMessage(error), 'error')
    }
  },

  openChat: async (chatId, viewerId) => {
    if (get().chatId === chatId && get().isWriter) return
    if (get().isSending) throw new Error('Wait for the current request to finish.')
    const operation = ++operationGeneration
    try {
      const history = await chatsApi.get(chatId)
      if (operation !== operationGeneration) return
      const isReadOnly = history.owner_id !== viewerId
      if (isReadOnly) chatClient.disconnect()
      else await chatClient.connect()
      if (operation !== operationGeneration) return
      set({ ...EMPTY_CHAT_STATE, chatId, isReadOnly })
      localStorage.setItem(ACTIVE_CHAT_KEY, chatId)
      applyHistory(history.title, history.turns)
      if (isReadOnly) return
      await chatClient.attach(chatId)
      if (operation !== operationGeneration) return
    } catch (error) {
      if (operation !== operationGeneration) return
      throw error
    }
  },

  deleteChat: async (chatId) => {
    await chatsApi.remove(chatId)
    if (get().chatId === chatId) get().reset()
    await get().refreshChats()
  },

  takeOver: async () => {
    await chatClient.takeOver()
  },

  selectPreview: (messageId) => {
    const message = get().messages.find((candidate) => candidate.id === messageId)
    if (!message?.completion?.result) return
    set({
      previewMessageId: messageId,
      result: message.completion.result,
      resultCompletedAt: message.completion.completedAt,
    })
  },

  loadTrace: async (messageId) => {
    const state = get()
    const message = state.messages.find((candidate) => candidate.id === messageId)
    const completion = message?.completion
    const runId = completion?.result?.run_id
    if (
      !runId ||
      completion.traceStatus === 'loading' ||
      completion.traceStatus === 'loaded'
    ) {
      return
    }
    const chatId = state.chatId
    updateTrace(messageId, runId, chatId, completion.trace, 'loading')
    try {
      const trace = await chatsApi.getRunTrace(runId)
      updateTrace(messageId, runId, chatId, trace.items, 'loaded')
    } catch {
      updateTrace(messageId, runId, chatId, [], 'error')
    }
  },

  reset: () => {
    operationGeneration += 1
    try {
      chatClient.disconnect()
      localStorage.removeItem(ACTIVE_CHAT_KEY)
    } catch {
      // Socket and localStorage teardown; failing it costs a stale chat id.
    }
    set({ ...EMPTY_CHAT_STATE })
  },

  clear: () => {
    get().reset()
    set({ chats: [], chatTotal: 0, initialized: false })
  },
}))

chatClient.subscribe((event) => {
  const state = useChatStore.getState()
  switch (event.type) {
    case 'ready':
      return
    case 'connection.closed':
      useChatStore.setState({
        isWriter: false,
        isSending: false,
        inFlightRequestId: null,
        awaitingAnswer: false,
        progress: [],
      })
      return
    case 'chat.attached': {
      if (event.chat_id !== state.chatId) return
      const preserveOptimistic =
        state.isSending && state.messages.some((item) => item.optimistic)
      if (!preserveOptimistic) applyHistory(event.title, event.turns)
      useChatStore.setState({
        title: event.title ?? state.title,
        isWriter: true,
        isDemoted: false,
        awaitingAnswer: false,
      })
      return
    }
    case 'chat.titled':
      if (event.chat_id !== state.chatId) return
      useChatStore.setState((current) => ({
        title: event.title,
        chats: current.chats.map((chat) =>
          chat.chat_id === event.chat_id ? { ...chat, title: event.title } : chat,
        ),
      }))
      void useChatStore.getState().refreshChats()
      return
    case 'chat.demoted':
      if (event.chat_id !== state.chatId) return
      useChatStore.setState({
        isWriter: false,
        isDemoted: true,
        awaitingAnswer: false,
      })
      return
    case 'status':
      if (event.request_id !== state.inFlightRequestId) return
      useChatStore.setState((current) => ({
        isSending: true,
        inFlightRequestId: event.request_id,
        progress: [...current.progress, { event: event.event, name: event.name }],
      }))
      return
    case 'question':
      if (!matchesCurrentRequest(event.request_id)) return
      appendAssistant(event.text)
      useChatStore.setState({ awaitingAnswer: true })
      refreshAfterTurn()
      return
    case 'answer':
      if (!matchesCurrentRequest(event.request_id)) return
      appendAssistant(event.text)
      refreshAfterTurn()
      return
    case 'result':
      if (!matchesCurrentRequest(event.request_id)) return
      completeRequest(event)
      refreshAfterTurn()
      return
    case 'aborted':
      if (!matchesCurrentRequest(event.request_id)) return
      appendAssistant(event.reason, 'warning')
      refreshAfterTurn()
      return
    case 'error':
      if (event.detail.code === 'not_writer') {
        useChatStore.setState({ isWriter: false, isDemoted: true })
      }
      if (event.request_id && matchesCurrentRequest(event.request_id)) {
        appendAssistant(event.detail.error, 'error')
      }
      if (event.detail.code === 'chat_not_found' && event.request_id) {
        localStorage.removeItem(ACTIVE_CHAT_KEY)
        useChatStore.setState({ ...EMPTY_CHAT_STATE })
        void useChatStore.getState().refreshChats()
      }
  }
})

function matchesCurrentRequest(requestId: string): boolean {
  const current = useChatStore.getState().inFlightRequestId
  return current === requestId
}

function appendAssistant(content: string, status?: ChatMessage['status']): void {
  useChatStore.setState((state) => ({
    messages: [
      ...state.messages,
      { id: nextMessageId(), role: 'assistant', content, status },
    ],
    isSending: false,
    inFlightRequestId: null,
    progress: [],
    awaitingAnswer: false,
  }))
}

function completeRequest(result: ChatResultFrame): void {
  const messageId = nextMessageId()
  const completedAt = new Date().toISOString()
  useChatStore.setState((state) => ({
    result,
    resultCompletedAt: completedAt,
    previewMessageId: messageId,
    messages: [
      ...state.messages,
      {
        id: messageId,
        role: 'assistant',
        content: summarize(result.text),
        completion: {
          elapsedMs: result.elapsed_ms ?? 0,
          completedAt,
          requestText:
            [...state.messages].reverse().find((message) => message.role === 'user')
              ?.content ?? '',
          result,
          trace: state.progress,
          traceStatus: 'loaded',
        },
      },
    ],
    isSending: false,
    inFlightRequestId: null,
    awaitingAnswer: false,
    progress: [],
  }))
}

function applyHistory(
  title: string | null,
  turns: ChatTurnDto[],
  traces = currentRunTraces(),
): void {
  const messages = turns.flatMap((turn) => turnToMessages(turn, traces))
  const latestCompletion = [...messages]
    .reverse()
    .find((message) => message.completion?.result)
  useChatStore.setState({
    title,
    messages,
    result: latestCompletion?.completion?.result ?? null,
    resultCompletedAt: latestCompletion?.completion?.completedAt ?? null,
    previewMessageId: latestCompletion?.id ?? null,
    awaitingAnswer: turns.at(-1)?.status === 'awaiting_input',
  })
}

function turnToMessages(
  turn: ChatTurnDto,
  traces: ReadonlyMap<string, StoredTrace>,
): ChatMessage[] {
  const user: ChatMessage = {
    id: `turn-${turn.sequence}-user`,
    role: 'user',
    content: turn.request_text,
  }
  const assistant = turnToAssistantMessage(turn, traces)
  return assistant ? [user, assistant] : [user]
}

function turnToAssistantMessage(
  turn: ChatTurnDto,
  traces: ReadonlyMap<string, StoredTrace>,
): ChatMessage | null {
  if (turn.status === 'completed' && turn.output_text) {
    const result = turnToResult(turn)
    const storedTrace = turn.run_id ? traces.get(turn.run_id) : undefined
    return {
      id: `turn-${turn.sequence}-assistant`,
      role: 'assistant',
      content: summarize(turn.output_text),
      completion: {
        elapsedMs: turn.elapsed_ms ?? 0,
        completedAt: turn.created_at,
        requestText: turn.request_text,
        result,
        trace: storedTrace?.items ?? [],
        traceStatus: storedTrace?.status ?? (turn.run_id ? 'idle' : 'loaded'),
      },
    }
  }
  if (turn.status === 'answered' && turn.output_text) {
    return {
      id: `turn-${turn.sequence}-assistant`,
      role: 'assistant',
      content: turn.output_text,
    }
  }
  if (turn.status === 'aborted' && turn.reason) {
    return {
      id: `turn-${turn.sequence}-assistant`,
      role: 'assistant',
      content: turn.reason,
      status: 'warning',
    }
  }
  if (turn.question) {
    return {
      id: `turn-${turn.sequence}-assistant`,
      role: 'assistant',
      content: turn.question,
      status: turn.status === 'abandoned' ? 'abandoned' : undefined,
    }
  }
  return null
}

interface StoredTrace {
  items: ChatProgress[]
  status: NonNullable<ChatMessage['completion']>['traceStatus']
}

function currentRunTraces(): Map<string, StoredTrace> {
  const traces = new Map<string, StoredTrace>()
  for (const message of useChatStore.getState().messages) {
    const runId = message.completion?.result?.run_id
    if (runId && message.completion) {
      traces.set(runId, {
        items: message.completion.trace,
        status: message.completion.traceStatus,
      })
    }
  }
  return traces
}

function updateTrace(
  messageId: string,
  runId: string,
  chatId: string | null,
  items: ChatProgress[],
  status: StoredTrace['status'],
): void {
  useChatStore.setState((state) => {
    if (
      state.chatId !== chatId ||
      !state.messages.some(
        (message) =>
          message.id === messageId && message.completion?.result?.run_id === runId,
      )
    ) {
      return state
    }
    return {
      messages: state.messages.map((message) =>
        message.completion?.result?.run_id === runId
          ? {
              ...message,
              completion: { ...message.completion, trace: items, traceStatus: status },
            }
          : message,
      ),
    }
  })
}

function turnToResult(turn: ChatTurnDto): ChatResultFrame {
  return {
    type: 'result',
    request_id: `history-${turn.sequence}`,
    title: turn.title,
    text: turn.output_text ?? '',
    severity: turn.severity,
    sop_id: turn.sop_id,
    sop: turn.sop,
    elapsed_ms: turn.elapsed_ms,
    run_id: turn.run_id,
  }
}

function refreshAfterTurn(): void {
  void useChatStore.getState().refreshChats()
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Could not send your request. Check your connection and try again.'
}

export function responseText(message: ChatClientEvent): string | null {
  switch (message.type) {
    case 'answer':
    case 'question':
      return message.text
    case 'result':
      return message.text
    case 'aborted':
      return message.reason
    case 'error':
      return message.detail.error
    default:
      return null
  }
}

function summarize(value: string): string {
  const plainText = value
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/\[([^\]]+)]\([^)]+\)/g, '$1')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return plainText.length > 220 ? `${plainText.slice(0, 219).trimEnd()}…` : plainText
}
